// Enchufe a React de `utils/filtrosPersistentes`. La regla y el almacén están
// allí, probados sin navegador; aquí sólo está lo que necesita el ciclo de vida
// del componente.
//
// Se llama UNA vez por pantalla, lo más arriba posible:
//
//   const { paramsIniciales, olvidar } = useFiltrosPersistentes('inventario')
//   const [empresa, setEmpresa] = useState(paramsIniciales.get('empresa') ?? '')
//
// El detalle que obliga a devolver `paramsIniciales` en vez de limitarse a
// reescribir la URL: los `useState(searchParams.get(…))` de las páginas corren
// en el PRIMER render, y para entonces la URL todavía está pelada. Si sólo se
// repusiera la URL desde un efecto, el estado ya se habría inicializado vacío y
// no volvería a leerse nunca —y en las pantallas cuyo rango de fechas es
// parámetro de la consulta al backend, además se habría disparado ya una
// petición con el rango equivocado—. Leyendo de `paramsIniciales` la pantalla
// arranca directamente con los filtros puestos.
import { useCallback, useEffect, useLayoutEffect, useMemo, useRef, useState } from 'react'
import { useSearchParams } from 'react-router-dom'
import {
  almacenSesion, guardarFiltros, leerFiltros, olvidarFiltros, resolverFiltros,
  type OrigenFiltros,
} from '@/utils/filtrosPersistentes'

export interface FiltrosPersistentes {
  /** Parámetros con los que la pantalla debe INICIALIZAR su estado: los de la
   *  URL si el usuario llegó con enlace, y si no los que recuerda la sesión. */
  paramsIniciales: URLSearchParams
  /** De dónde salieron. Útil sobre todo para depurar. */
  origen: OrigenFiltros
  /** Olvida lo guardado de esta pantalla. No hace falta llamarlo desde un
   *  «Limpiar filtros» que vacíe la URL —eso ya borra el recuerdo solo—, pero
   *  está para los que sólo borran algunas claves. */
  olvidar: () => void
}

/**
 * Recuerda durante la sesión los filtros de una pantalla.
 *
 * `ambito` es la clave de persistencia y debe ser única por pantalla
 * («inventario», «produccionDiaria», «reports.rawMaterials»). Nunca se deriva
 * del pathname automáticamente: las rutas con parámetros («/reports/:report»)
 * necesitan una clave por variante, y hacerlo explícito evita que dos pantallas
 * distintas compartan filtros por accidente.
 */
export function useFiltrosPersistentes(ambito: string): FiltrosPersistentes {
  const [searchParams, setSearchParams] = useSearchParams()

  // Se decide UNA sola vez, en el primer render. A partir de ahí la fuente de
  // verdad vuelve a ser la URL y esto no se vuelve a mirar.
  const [inicial] = useState(() => {
    const { query, origen } = resolverFiltros(
      searchParams.toString(),
      leerFiltros(ambito, almacenSesion()),
    )
    return { params: new URLSearchParams(query), origen }
  })

  // Reponer en la URL lo restaurado, para que la pantalla siga siendo enlazable
  // y para que los filtros activos que se pintan desde la URL (los reports) la
  // vean. En efecto de LAYOUT: la navegación entra antes de pintar y no se ve el
  // parpadeo de una tabla sin filtrar.
  const repuesto = useRef(false)
  useLayoutEffect(() => {
    if (repuesto.current) return
    repuesto.current = true
    if (inicial.origen === 'sesion') setSearchParams(inicial.params, { replace: true })
    // Sólo al montar: repetirlo pisaría cualquier filtro que el usuario cambiase.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [])

  // Guardar en cada cambio de la query string. La invariante es «lo guardado es
  // lo que dice la URL», y de ahí sale gratis que limpiar filtros limpie también
  // el recuerdo (la URL vacía borra la entrada).
  const restaurando = useRef(inicial.origen === 'sesion')
  useEffect(() => {
    // El primer pase tras restaurar todavía ve la URL pelada de este render: si
    // guardase, borraría justo lo que acabamos de reponer. El commit siguiente
    // —ya con la URL restaurada— sí guarda.
    if (restaurando.current) { restaurando.current = false; return }
    guardarFiltros(ambito, searchParams.toString(), almacenSesion())
  }, [ambito, searchParams])

  // Memoizado: `olvidar` acaba en las dependencias de los `useCallback` de las
  // pantallas (el «Limpiar filtros» de los reports), y una función nueva por
  // render arrastraría a re-renderizar la barra de filtros entera.
  const olvidar = useCallback(() => olvidarFiltros(ambito, almacenSesion()), [ambito])

  return useMemo(
    () => ({ paramsIniciales: inicial.params, origen: inicial.origen, olvidar }),
    [inicial, olvidar],
  )
}
